import { comma } from "@library/Utilities";
import type { IVehicle } from "../IVehicle";
import { ConditionState } from "./ConditionState";
import { ConditionType } from "./ConditionType";
import type { ICondition } from "./ICondition";
import type { IConditionLabelOptions, IConditionLabels } from "./IConditionLabels";

export abstract class ConditionLabels
{

    labels: IConditionLabels = {} as IConditionLabels;
    
    /**
     * Updates the labels on the condition.  
     * @param condition 
     */
    updateLabels( vehicle: IVehicle, condition: ICondition )
    {

        const custom = this.getLabels();
        const labels = {
            description: this.getDescription( condition ),
            stateLabel: ConditionState.toString( condition.state ),
            intervalState: this.getLabel( vehicle, condition ),
            intervalDescription: this.getIntervalDescription( vehicle, condition ),
            intervalMessage: this.getIntervalMessage( condition ),
            actionMessage: this.getActionMessage(),
            dateLabel: condition.date.label,
            odometerLabel: condition.odometer.label,
            dateRelativeLabel: condition.date.relativeLabel,
            odometerRelativeLabel: condition.odometer.relativeLabel,
            protectedByLabel: 'You are protected by'
        } as IConditionLabels;

        Object.assign( labels, custom );
        labels.intervalNote = this.getIntervalNote( condition, labels );
        
        this.labels = labels;

    }

    /**
     * Gets the action message for the service note
     */
    abstract getActionMessage(): string;

    /**
     * Gets the labels for the condition
     */
    abstract getLabels(): IConditionLabelOptions;

    /**
     * Gets the description.  Can be overridden
     */
    protected getDescription( condition: ICondition )
    {
        if( condition.state == ConditionState.Critical )
        {
            return `The Health Monitor has detected<br/>a ${ ConditionType.toString( condition.type ) } issue`;
        } 
        return `${ ConditionType.toString( condition.type ) } is ${ ConditionState.toString( condition.state ).toLowerCase() }`;
    } 

    /**
     * Gets the label.  Can be overridden
     */
    protected getLabel( vehicle: IVehicle, condition: ICondition )
    {

        const hasOdometer = condition.odometer.isIncomplete == false;
        const hasDate = condition.date.isIncomplete == false;
        const isDatePreferred = hasDate && condition.date.score <= condition.odometer.score;
        const isOdometerPreferred = hasOdometer && condition.date.score > condition.odometer.score;

        if( isDatePreferred )
        {
            return condition.date.label;
        }

        else if( isOdometerPreferred )
        {
            return condition.date.getLabelFromOdometer( condition.odometer.current, condition.odometer.next, vehicle.estimatedAnnualKilometers );
            //return this.odometer.label;
        }
            
        else if( hasDate )
        {
            return condition.date.label;
        }

        else if( hasOdometer )
        {
            return condition.date.getLabelFromOdometer( condition.odometer.current, condition.odometer.next, vehicle.estimatedAnnualKilometers );
            //return this.odometer.label;
        }

        // Neither date or odometer
        return 'n/a';   

    }

    /**
     * Gets the status message for the condition
     * @param condition 
     * @returns 
     */
    protected getIntervalMessage( condition: ICondition )
    {
        
        if( condition.state == ConditionState.Critical )
        {
            return 'Contact your dealership as soon as possible';    
        }

        if( this.labels.intervalState != null && this.labels.intervalState != '' )
        {
            return this.labels.intervalState;
        }

        return 'Resolve as soon as possible';
        
    }
    
    /**
     * Gets the interval note message for the condition
     * @param labels 
     * @returns 
     */
    protected getIntervalNote(  condition: ICondition, labels: IConditionLabels )
    {

        const name = labels.name.toLowerCase();

        if( condition.state == ConditionState.Critical )
        {
            return 'Contact your dealership as soon as possible';    
        }

        if( labels.stateLabel != null && labels.intervalState != null )
        {
            const status = labels.stateLabel?.toLowerCase();
            const interval = labels.intervalState?.toLowerCase();
            return `You have a ${name} ${status} ${interval}`;
        }

        return `You have a ${name} alert`;
        
    }

    /**
     * Gets the label that depicts the frequency of updates for the condition
     * @returns 
     */
    protected getIntervalDescription( vehicle: IVehicle, condition: ICondition )
    {
        if( condition.averageOdometerInterval == 0 ) return 'n/a';
        const kms = comma( condition.averageOdometerInterval );
        const months = Math.ceil( condition.averageOdometerInterval / vehicle.estimatedAnnualKilometers * 12 );
        const date = months == 1 ? 'month' : `${ months } months`;
        return `every ${ kms }km or every ${ date } on average`;
    }

}